import { 
  getFirestore, 
  doc, 
  collection, 
  getDoc, 
  getDocs, 
  setDoc, 
  updateDoc, 
  writeBatch,
  increment 
} from 'firebase/firestore';
import { db, setDocWithQueue } from './firebase';
import { 
  BioBoxType, 
  BioCrateType,
  BioBoxRarity, 
  BioCrateRarity,
  LootItem, 
  BioBoxInventoryItem, 
  BioCrateInventoryItem,
  UserConsumableItem, 
  GamificationProfile, 
  NutriTokenLedgerEntry, 
  AuditTrackingEvent,
  VaultShopItem 
} from '../types';
import { 
  BIO_BOX_DEFINITIONS, 
  RARITY_WEIGHTS, 
  VAULT_SHOP_CATALOGUE, 
  calculateDemographicCohort, 
  resolveDemographicTitle,
  PERKS_REGISTRY 
} from '../data/gamificationEconomyConfig';

const LOCAL_GAMIFICATION_KEY = 'main_nutrition_gamification_profile';
const LOCAL_INVENTORY_KEY = 'main_nutrition_loot_inventory';
const LOCAL_BOXES_KEY = 'main_nutrition_bio_boxes';
const LOCAL_CRATES_KEY = 'main_nutrition_bio_crates';

// Default initial state
export const DEFAULT_GAMIFICATION_PROFILE: GamificationProfile = {
  token_balance: 100, // Welcome gift
  current_title_id: 'genz_t1_m',
  current_title_name: 'Sigma Cadet',
  current_tier: 1,
  cohort: 'gen_z',
  tone_preference: 'bro',
  streak_freezes_available: 0,
  unlocked_themes: ['default'],
  active_theme: 'default',
  total_boxes_opened: 0,
  total_crates_opened: 0,
  total_tokens_earned: 100,
  active_perks: ['token_boost_5']
};

/**
 * Get or load user's gamification profile (with instant local cache fallback)
 */
export async function getGamificationProfile(userId?: string): Promise<GamificationProfile> {
  if (!userId) {
    const cached = localStorage.getItem(LOCAL_GAMIFICATION_KEY);
    return cached ? JSON.parse(cached) : DEFAULT_GAMIFICATION_PROFILE;
  }

  try {
    const userDocRef = doc(db, 'users', userId);
    const snap = await getDoc(userDocRef);

    if (snap.exists()) {
      const data = snap.data();
      const gamState = data.gamification_profile || data.gamification || {};
      const profile: GamificationProfile = {
        ...DEFAULT_GAMIFICATION_PROFILE,
        ...gamState,
        token_balance: typeof gamState.token_balance === 'number' ? gamState.token_balance : 100,
        unlocked_themes: gamState.unlocked_themes || ['default'],
        active_perks: gamState.active_perks || ['token_boost_5'],
      };
      localStorage.setItem(LOCAL_GAMIFICATION_KEY, JSON.stringify(profile));
      return profile;
    }
  } catch (e) {
    console.warn('[BioBoxService] Error fetching gamification profile:', e);
  }

  const cached = localStorage.getItem(LOCAL_GAMIFICATION_KEY);
  return cached ? JSON.parse(cached) : DEFAULT_GAMIFICATION_PROFILE;
}

/**
 * Get user's active Bio-Boxes from inventory
 */
export async function getUserBioBoxes(userId?: string): Promise<BioBoxInventoryItem[]> {
  if (!userId) {
    const cached = localStorage.getItem(LOCAL_BOXES_KEY) || localStorage.getItem(LOCAL_CRATES_KEY);
    return cached ? JSON.parse(cached) : [];
  }

  try {
    const invCol = collection(db, 'users', userId, 'loot_inventory');
    const snap = await getDocs(invCol);
    const boxes: BioBoxInventoryItem[] = [];

    snap.forEach((d) => {
      const item = d.data();
      if (item.type === 'box' || item.type === 'crate' || item.boxType || item.crateType) {
        boxes.push({
          id: d.id,
          boxType: item.boxType || item.crateType || 'daily_supply',
          crateType: item.boxType || item.crateType || 'daily_supply',
          obtainedAt: item.obtainedAt || Date.now(),
          source: item.source || 'Daily Drop',
          seed: item.seed || Math.random().toString(36).substring(2)
        });
      }
    });

    if (boxes.length > 0) {
      localStorage.setItem(LOCAL_BOXES_KEY, JSON.stringify(boxes));
      localStorage.setItem(LOCAL_CRATES_KEY, JSON.stringify(boxes));
      return boxes;
    } else {
      localStorage.setItem(LOCAL_BOXES_KEY, JSON.stringify([]));
      localStorage.setItem(LOCAL_CRATES_KEY, JSON.stringify([]));
      return [];
    }
  } catch (e) {
    console.warn('[BioBoxService] Error fetching user bio-boxes:', e);
  }

  const cached = localStorage.getItem(LOCAL_BOXES_KEY) || localStorage.getItem(LOCAL_CRATES_KEY);
  return cached ? JSON.parse(cached) : [];
}
export const getUserBioCrates = getUserBioBoxes;

/**
 * Get user's consumables (Streak Freezes, Pro Vouchers, etc.)
 */
export async function getUserConsumables(userId?: string): Promise<UserConsumableItem[]> {
  if (!userId) {
    const cached = localStorage.getItem(LOCAL_INVENTORY_KEY);
    return cached ? JSON.parse(cached) : [];
  }

  try {
    const invCol = collection(db, 'users', userId, 'loot_inventory');
    const snap = await getDocs(invCol);
    const consumables: UserConsumableItem[] = [];

    snap.forEach((d) => {
      const item = d.data();
      if (item.type !== 'box' && item.type !== 'crate' && !item.boxType && !item.crateType) {
        consumables.push({
          id: d.id,
          type: item.type || 'streak_freeze',
          itemId: item.itemId || d.id,
          count: item.count || 1,
          name: item.name || { en: 'Consumable Item', ua: 'Предмет' },
          description: item.description || { en: '', ua: '' },
          icon: item.icon || 'Shield',
          acquiredAt: item.acquiredAt || Date.now(),
          expiresAt: item.expiresAt,
          metadata: item.metadata
        });
      }
    });

    if (consumables.length > 0) {
      localStorage.setItem(LOCAL_INVENTORY_KEY, JSON.stringify(consumables));
      return consumables;
    }
  } catch (e) {
    console.warn('[BioBoxService] Error fetching user consumables:', e);
  }

  const cached = localStorage.getItem(LOCAL_INVENTORY_KEY);
  return cached ? JSON.parse(cached) : [];
}

/**
 * Award a Bio-Box to user's inventory
 */
export async function awardBioBox(
  userId: string, 
  boxType: BioBoxType, 
  source: string
): Promise<BioBoxInventoryItem> {
  const boxId = `box_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;
  const seed = `${Date.now()}_${Math.random().toString(36).substring(2, 10)}`;

  const newBox: BioBoxInventoryItem = {
    id: boxId,
    boxType,
    crateType: boxType,
    obtainedAt: Date.now(),
    source,
    seed
  };

  // Update local cache immediately
  const localBoxes = await getUserBioBoxes(userId);
  localBoxes.push(newBox);
  localStorage.setItem(LOCAL_BOXES_KEY, JSON.stringify(localBoxes));
  localStorage.setItem(LOCAL_CRATES_KEY, JSON.stringify(localBoxes));

  // Write to Firestore
  try {
    const boxDocRef = doc(db, 'users', userId, 'loot_inventory', boxId);
    await setDocWithQueue(boxDocRef, {
      ...newBox,
      type: 'box'
    });

    await logAuditEvent(userId, 'BOX_AWARDED', {
      boxId,
      boxType,
      source
    }, seed);

  } catch (e) {
    console.warn('[BioBoxService] Failed to persist awarded bio-box to Firestore:', e);
  }

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('bio-boxes-updated'));
  }

  return newBox;
}
export const awardBioCrate = awardBioBox;

/**
 * Check and award Daily Supply Bio-Box if eligible
 */
export async function checkAndAwardDailyBox(userId: string): Promise<boolean> {
  if (!userId) return false;

  const todayStr = new Date().toISOString().split('T')[0];
  const lastAwardKey = `daily_box_awarded_${todayStr}`;
  if (localStorage.getItem(lastAwardKey)) {
    return false;
  }

  try {
    const profile = await getGamificationProfile(userId);
    if (profile.last_daily_box_date === todayStr || profile.last_daily_crate_date === todayStr) {
      localStorage.setItem(lastAwardKey, 'true');
      return false;
    }

    await awardBioBox(userId, 'daily_supply', 'Daily First Log Bonus');

    // Update profile date
    profile.last_daily_box_date = todayStr;
    profile.last_daily_crate_date = todayStr;
    localStorage.setItem(LOCAL_GAMIFICATION_KEY, JSON.stringify(profile));
    localStorage.setItem(lastAwardKey, 'true');

    const userDocRef = doc(db, 'users', userId);
    await setDocWithQueue(userDocRef, {
      gamification_profile: {
        last_daily_box_date: todayStr,
        last_daily_crate_date: todayStr
      }
    }, { merge: true });

    return true;
  } catch (e) {
    console.warn('[BioBoxService] Error checking daily box:', e);
    return false;
  }
}
export const checkAndAwardDailyCrate = checkAndAwardDailyBox;

/**
 * Check and award Streak Matrix Bio-Box on milestone days
 */
export async function checkAndAwardStreakBox(userId: string, currentStreak: number): Promise<boolean> {
  if (!userId || currentStreak < 3) return false;

  const milestoneDays = [3, 7, 14, 21, 28, 60, 90, 180, 365];
  if (!milestoneDays.includes(currentStreak)) return false;

  const streakBoxKey = `streak_box_awarded_day_${currentStreak}`;
  if (localStorage.getItem(streakBoxKey)) {
    return false;
  }

  try {
    await awardBioBox(userId, 'streak_matrix', `${currentStreak}-Day Streak Milestone`);
    localStorage.setItem(streakBoxKey, 'true');
    return true;
  } catch (e) {
    console.warn('[BioBoxService] Error awarding streak bio-box:', e);
    return false;
  }
}
export const checkAndAwardStreakCrate = checkAndAwardStreakBox;

/**
 * Check and award Perfect Macro Bio-Box Vault when hitting macros within ±5%
 */
export async function checkAndAwardPerfectMacroBox(
  userId: string,
  targetCalories: number,
  loggedCalories: number,
  targetProtein: number,
  loggedProtein: number
): Promise<boolean> {
  if (!userId || targetCalories <= 0 || targetProtein <= 0) return false;

  const todayStr = new Date().toISOString().split('T')[0];
  const macroBoxKey = `perfect_macro_box_${todayStr}`;
  if (localStorage.getItem(macroBoxKey)) return false;

  const calDiff = Math.abs(loggedCalories - targetCalories) / targetCalories;
  const proDiff = Math.abs(loggedProtein - targetProtein) / targetProtein;

  // Within ±5% tolerance on both calories and protein
  if (calDiff <= 0.05 && proDiff <= 0.05) {
    try {
      await awardBioBox(userId, 'perfect_macro', 'Perfect Macro Calibration (±5%)');
      localStorage.setItem(macroBoxKey, 'true');
      return true;
    } catch (e) {
      console.warn('[BioBoxService] Error awarding perfect macro bio-box:', e);
      return false;
    }
  }

  return false;
}
export const checkAndAwardPerfectMacroCrate = checkAndAwardPerfectMacroBox;

/**
 * Roll an unboxing reward based on weighted probabilities and luck modifier
 */
export function rollBoxRewards(
  boxType: BioBoxType, 
  userLuckModifier: number = 1.0
): { rarity: BioBoxRarity; tokenPayout: number; items: LootItem[] } {
  const boxMeta = BIO_BOX_DEFINITIONS[boxType] || BIO_BOX_DEFINITIONS.daily_supply;

  // 1. Calculate Rarity Roll
  const adjustedWeights = { ...RARITY_WEIGHTS };
  if (userLuckModifier > 1.0) {
    adjustedWeights.epic = Math.round(adjustedWeights.epic * userLuckModifier);
    adjustedWeights.legendary = Math.round(adjustedWeights.legendary * userLuckModifier);
    adjustedWeights.mythic = Math.round(adjustedWeights.mythic * userLuckModifier);
  }

  // Force minimum guaranteed rarity
  if (boxMeta.guaranteedRarity === 'rare') {
    adjustedWeights.common = 0;
  } else if (boxMeta.guaranteedRarity === 'epic') {
    adjustedWeights.common = 0;
    adjustedWeights.rare = 0;
  }

  const totalWeight = Object.values(adjustedWeights).reduce((a, b) => a + b, 0);
  const rand = Math.random() * totalWeight;

  let cumulative = 0;
  let rolledRarity: BioBoxRarity = 'common';

  for (const [r, weight] of Object.entries(adjustedWeights)) {
    cumulative += weight;
    if (rand <= cumulative) {
      rolledRarity = r as BioBoxRarity;
      break;
    }
  }

  // 2. Calculate Token Amount
  const baseRange = boxMeta.maxTokens - boxMeta.minTokens;
  const tokenRandomFraction = Math.random();
  let tokenPayout = Math.round(boxMeta.minTokens + baseRange * tokenRandomFraction);

  if (rolledRarity === 'epic') tokenPayout = Math.round(tokenPayout * 1.25);
  if (rolledRarity === 'legendary') tokenPayout = Math.round(tokenPayout * 1.6);
  if (rolledRarity === 'mythic') tokenPayout = Math.round(tokenPayout * 2.2);

  // 3. Generate Primary Loot Item
  const items: LootItem[] = [];

  if (rolledRarity === 'mythic') {
    items.push({
      id: `loot_pro_7d_${Date.now()}`,
      name: { ua: 'PRO Ваучер на 7 Днів', en: '7-Day PRO Voucher' },
      type: 'pro_voucher_7d',
      rarity: 'mythic',
      icon: 'Crown',
      description: {
        ua: 'Міфічний ваучер на 7 днів необмеженого преміум доступу!',
        en: 'Mythic voucher granting 7 days of full PRO membership access!',
        broFlavorUA: 'Міфічний дроп! Тиждень повного домінування! 👑',
        broFlavorEN: 'Mythic payout! 7 days of pure godmode unlocked! 👑'
      }
    });
  } else if (rolledRarity === 'legendary') {
    const isPass = Math.random() > 0.4;
    if (isPass) {
      items.push({
        id: `loot_pro_48h_${Date.now()}`,
        name: { ua: 'PRO Пропуск на 48 Годин', en: '48-Hour PRO Pass' },
        type: 'pro_voucher_48h',
        rarity: 'legendary',
        icon: 'Sparkles',
        description: {
          ua: 'Легендарний пропуск на 2 доби PRO-аналітики та рецептів.',
          en: 'Legendary 48-hour all-access PRO pass.',
          broFlavorUA: 'Легендарний пропуск у кишені! Вмикай режим звіра 🔥',
          broFlavorEN: 'Legendary pass secured! Time to lock in 🔥'
        }
      });
    } else {
      items.push({
        id: `loot_theme_cyber_${Date.now()}`,
        name: { ua: 'Тема: Кіберпанк Матриця', en: 'Theme: Cyberpunk Matrix' },
        type: 'theme_unlock',
        rarity: 'legendary',
        valueString: 'cyberpunk',
        icon: 'Palette',
        description: {
          ua: 'Ексклюзивний кібернетичний дизайн інтерфейсу.',
          en: 'Exclusive cybernetic interface styling theme.',
          broFlavorUA: 'Неоновий матричний стиль для справжнього профі 🟢',
          broFlavorEN: 'Neon matrix cyber theme ready to equip 🟢'
        }
      });
    }
  } else if (rolledRarity === 'epic') {
    items.push({
      id: `loot_pro_24h_${Date.now()}`,
      name: { ua: 'PRO Доступ на 24 Години', en: '24-Hour PRO Pass' },
      type: 'pro_voucher_24h',
      rarity: 'epic',
      icon: 'Zap',
      description: {
        ua: 'Доба безлімітного ШІ-аналізу та біо-хакінг маркерів.',
        en: '24 hours of full AI vision analysis and biomarker tracking.',
        broFlavorUA: 'Епічний дроп! Доба турбо-режиму на повну котушку ⚡',
        broFlavorEN: 'Epic drop! 24 hours of turbo AI vision unlocked ⚡'
      }
    });
  } else if (rolledRarity === 'rare') {
    items.push({
      id: `loot_freeze_${Date.now()}`,
      name: { ua: 'Заморозка Стріку', en: 'Streak Freeze Shield' },
      type: 'streak_freeze',
      rarity: 'rare',
      icon: 'Shield',
      description: {
        ua: 'Захистить ваш стрік, якщо ви випадково пропустите запис.',
        en: 'Protects your streak from breaking if you miss a day.',
        broFlavorUA: 'Щит для твого стріку! Спи спокійно, бро 🛡️',
        broFlavorEN: 'Streak shield locked into inventory 🛡️'
      }
    });
  } else {
    items.push({
      id: `loot_token_pack_${Date.now()}`,
      name: { ua: 'Пакет Nutri-Токенів', en: 'Nutri-Token Cache' },
      type: 'tokens',
      rarity: 'common',
      amount: tokenPayout,
      icon: 'Coins',
      description: {
        ua: `Отримано ${tokenPayout} токенів для сховища винагород.`,
        en: `Received ${tokenPayout} Nutri-Tokens for the Redemption Vault.`,
        broFlavorUA: 'Токени в скарбничку! Збирай на топові призи 💰',
        broFlavorEN: 'Tokens added to balance! Keep stacking them up 💰'
      }
    });
  }

  return {
    rarity: rolledRarity,
    tokenPayout,
    items
  };
}
export const rollCrateRewards = rollBoxRewards;

/**
 * Execute unboxing state mutation: removes Bio-Box, awards tokens and items, records audit event
 */
export async function executeUnboxBioBox(
  userId?: string, 
  boxItem?: BioBoxInventoryItem,
  userLuckModifier: number = 1.0
): Promise<{ rarity: BioBoxRarity; tokenPayout: number; items: LootItem[] }> {
  if (!boxItem) {
    return {
      rarity: 'common',
      tokenPayout: 25,
      items: []
    };
  }

  const boxType = boxItem.boxType || boxItem.crateType || 'daily_supply';
  const result = rollBoxRewards(boxType, userLuckModifier);

  // 1. Update local cache immediately (instant optimistic update)
  let localBoxes: BioBoxInventoryItem[] = [];
  try {
    const cached = localStorage.getItem(LOCAL_BOXES_KEY) || localStorage.getItem(LOCAL_CRATES_KEY);
    if (cached) localBoxes = JSON.parse(cached);
  } catch (e) {}
  localBoxes = localBoxes.filter(c => c.id !== boxItem.id);
  localStorage.setItem(LOCAL_BOXES_KEY, JSON.stringify(localBoxes));
  localStorage.setItem(LOCAL_CRATES_KEY, JSON.stringify(localBoxes));

  let profile: GamificationProfile = { ...DEFAULT_GAMIFICATION_PROFILE };
  try {
    const cached = localStorage.getItem(LOCAL_GAMIFICATION_KEY);
    if (cached) profile = { ...DEFAULT_GAMIFICATION_PROFILE, ...JSON.parse(cached) };
  } catch (e) {}

  profile.token_balance = (profile.token_balance || 0) + result.tokenPayout;
  profile.total_tokens_earned = (profile.total_tokens_earned || 0) + result.tokenPayout;
  profile.total_boxes_opened = (profile.total_boxes_opened || profile.total_crates_opened || 0) + 1;
  profile.total_crates_opened = profile.total_boxes_opened;

  // Process items into local inventory
  let localConsumables: UserConsumableItem[] = [];
  try {
    const cached = localStorage.getItem(LOCAL_INVENTORY_KEY);
    if (cached) localConsumables = JSON.parse(cached);
  } catch (e) {}

  result.items.forEach(item => {
    if (item.type === 'streak_freeze') {
      profile.streak_freezes_available = (profile.streak_freezes_available || 0) + 1;
      const existing = localConsumables.find(c => c.type === 'streak_freeze');
      if (existing) {
        existing.count += 1;
      } else {
        localConsumables.push({
          id: `consumable_freeze_${Date.now()}`,
          type: 'streak_freeze',
          itemId: 'streak_freeze',
          count: 1,
          name: item.name,
          description: item.description,
          icon: 'Shield',
          acquiredAt: Date.now()
        });
      }
    } else if (item.type.startsWith('pro_voucher')) {
      const hours = item.type === 'pro_voucher_7d' ? 168 : item.type === 'pro_voucher_48h' ? 48 : 24;
      const existing = localConsumables.find(c => c.itemId === item.type);
      if (existing) {
        existing.count += 1;
      } else {
        localConsumables.push({
          id: `consumable_${item.type}_${Date.now()}`,
          type: item.type as any,
          itemId: item.type,
          count: 1,
          name: item.name,
          description: item.description,
          icon: item.icon,
          acquiredAt: Date.now(),
          metadata: { proDurationHours: hours }
        });
      }
    } else if (item.type === 'theme_unlock' && item.valueString) {
      if (!profile.unlocked_themes.includes(item.valueString)) {
        profile.unlocked_themes.push(item.valueString);
      }
    }
  });

  localStorage.setItem(LOCAL_GAMIFICATION_KEY, JSON.stringify(profile));
  localStorage.setItem(LOCAL_INVENTORY_KEY, JSON.stringify(localConsumables));

  // 2. Persist to Firestore in background (non-blocking)
  if (userId && userId !== 'local_user' && userId !== 'guest_user') {
    (async () => {
      try {
        const userDocRef = doc(db, 'users', userId);
        const boxDocRef = doc(db, 'users', userId, 'loot_inventory', boxItem.id);

        const batch = writeBatch(db);
        batch.delete(boxDocRef);
        batch.set(userDocRef, {
          gamification_profile: {
            token_balance: profile.token_balance,
            total_tokens_earned: profile.total_tokens_earned,
            total_boxes_opened: profile.total_boxes_opened,
            total_crates_opened: profile.total_boxes_opened,
            streak_freezes_available: profile.streak_freezes_available,
            unlocked_themes: profile.unlocked_themes,
            active_theme: profile.active_theme
          }
        }, { merge: true });

        await batch.commit();

        await logAuditEvent(userId, 'BOX_OPENED', {
          boxId: boxItem.id,
          boxType: boxType,
          rolledRarity: result.rarity,
          tokenPayout: result.tokenPayout,
          items: result.items
        }, boxItem.seed);

      } catch (e) {
        console.warn('[BioBoxService] Failed to commit unboxing batch to Firestore:', e);
      }
    })().catch(() => {});
  }

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('bio-boxes-updated'));
    window.dispatchEvent(new CustomEvent('gamification-profile-updated'));
  }

  return result;
}
export const executeUnboxCrate = executeUnboxBioBox;

/**
 * Equip an unlocked UI theme from the user's gamification inventory
 */
export async function equipTheme(
  userId: string,
  themeId: string
): Promise<{ success: boolean; message: { en: string; ua: string } }> {
  const profile = await getGamificationProfile(userId);
  if (!profile.unlocked_themes.includes(themeId) && themeId !== 'default') {
    return {
      success: false,
      message: {
        en: 'Theme is not yet unlocked in your collection.',
        ua: 'Ця тема ще не відкрита у вашій колекції.'
      }
    };
  }

  profile.active_theme = themeId;
  localStorage.setItem(LOCAL_GAMIFICATION_KEY, JSON.stringify(profile));
  localStorage.setItem('app_skin', themeId);

  if (userId && userId !== 'local_user' && userId !== 'guest_user') {
    try {
      const userDocRef = doc(db, 'users', userId);
      await setDocWithQueue(userDocRef, {
        gamification_profile: {
          active_theme: themeId,
          unlocked_themes: profile.unlocked_themes
        },
        skin: themeId
      }, { merge: true });
    } catch (e) {
      console.warn('[BioBoxService] Error syncing equipped theme to Firestore:', e);
    }
  }

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('gamification-profile-updated'));
  }

  return {
    success: true,
    message: {
      en: 'Theme equipped successfully!',
      ua: 'Тему успішно активовано!'
    }
  };
}

/**
 * Purchase item from Redemption Vault shop
 */
export async function purchaseVaultShopItem(
  userId: string, 
  shopItem: VaultShopItem
): Promise<{ success: boolean; message: { en: string; ua: string } }> {
  const profile = await getGamificationProfile(userId);

  if (profile.token_balance < shopItem.tokenPrice) {
    return {
      success: false,
      message: {
        en: `Insufficient Nutri-Tokens (${profile.token_balance}/${shopItem.tokenPrice} NTK required).`,
        ua: `Недостатньо Nutri-Токенів (потрібно ${shopItem.tokenPrice} NTK, у вас ${profile.token_balance}).`
      }
    };
  }

  // Deduct tokens
  profile.token_balance -= shopItem.tokenPrice;

  // Apply action
  if ((shopItem.actionType === 'add_box' || shopItem.actionType === 'add_crate') && (shopItem.payload.boxType || shopItem.payload.crateType)) {
    const targetBoxType = shopItem.payload.boxType || shopItem.payload.crateType || 'daily_supply';
    const qty = shopItem.payload.quantity || 1;
    for (let i = 0; i < qty; i++) {
      await awardBioBox(userId, targetBoxType, 'Redemption Vault Purchase');
    }
  } else if (shopItem.actionType === 'activate_pro' && shopItem.payload.proDurationHours) {
    const durationMs = shopItem.payload.proDurationHours * 3600 * 1000;
    const currentExpiry = profile.pro_expires_at && profile.pro_expires_at > Date.now() 
      ? profile.pro_expires_at 
      : Date.now();
    profile.pro_expires_at = currentExpiry + durationMs;

    try {
      const userRef = doc(db, 'users', userId);
      await setDocWithQueue(userRef, {
        subscriptionTier: 'pro',
        pro_expires_at: profile.pro_expires_at
      }, { merge: true });
    } catch (e) {}
  } else if (shopItem.actionType === 'unlock_theme' && shopItem.payload.themeId) {
    if (!profile.unlocked_themes.includes(shopItem.payload.themeId)) {
      profile.unlocked_themes.push(shopItem.payload.themeId);
    }
    profile.active_theme = shopItem.payload.themeId;
  } else if (shopItem.actionType === 'add_consumable' && shopItem.payload.consumableType === 'streak_freeze') {
    profile.streak_freezes_available = (profile.streak_freezes_available || 0) + (shopItem.payload.quantity || 1);
  }

  localStorage.setItem(LOCAL_GAMIFICATION_KEY, JSON.stringify(profile));

  // Sync to Firestore
  try {
    const userDocRef = doc(db, 'users', userId);
    await setDocWithQueue(userDocRef, {
      gamification_profile: {
        token_balance: profile.token_balance,
        pro_expires_at: profile.pro_expires_at || null,
        streak_freezes_available: profile.streak_freezes_available,
        unlocked_themes: profile.unlocked_themes,
        active_theme: profile.active_theme
      }
    }, { merge: true });

    await logAuditEvent(userId, 'TOKEN_SPENT', {
      shopItemId: shopItem.id,
      tokenPrice: shopItem.tokenPrice,
      actionType: shopItem.actionType,
      balanceAfter: profile.token_balance
    });

  } catch (e) {
    console.warn('[BioBoxService] Error writing shop purchase to Firestore:', e);
  }

  return {
    success: true,
    message: {
      en: `Successfully purchased ${shopItem.name.en}!`,
      ua: `Успішно придбано: ${shopItem.name.ua}!`
    }
  };
}

/**
 * Use a consumable item from inventory (e.g. activate PRO pass voucher)
 */
export async function activateConsumableItem(
  userId: string, 
  consumable: UserConsumableItem
): Promise<{ success: boolean; message: { en: string; ua: string } }> {
  const profile = await getGamificationProfile(userId);
  const consumables = await getUserConsumables(userId);

  const target = consumables.find(c => c.id === consumable.id || c.itemId === consumable.itemId);
  if (!target || target.count <= 0) {
    return {
      success: false,
      message: {
        en: 'Item not found in inventory.',
        ua: 'Предмет не знайдено в інвентарі.'
      }
    };
  }

  // Consume 1 count
  target.count -= 1;
  const filtered = consumables.filter(c => c.count > 0);
  localStorage.setItem(LOCAL_INVENTORY_KEY, JSON.stringify(filtered));

  if (target.itemId.startsWith('pro_voucher') || target.type.startsWith('pro_pass')) {
    const hours = target.metadata?.proDurationHours || (target.itemId.includes('7d') ? 168 : target.itemId.includes('48h') ? 48 : 24);
    const durationMs = hours * 3600 * 1000;
    const currentExpiry = profile.pro_expires_at && profile.pro_expires_at > Date.now() 
      ? profile.pro_expires_at 
      : Date.now();
    profile.pro_expires_at = currentExpiry + durationMs;

    try {
      const userRef = doc(db, 'users', userId);
      await setDocWithQueue(userRef, {
        subscriptionTier: 'pro',
        pro_expires_at: profile.pro_expires_at
      }, { merge: true });
    } catch (e) {}
  }

  localStorage.setItem(LOCAL_GAMIFICATION_KEY, JSON.stringify(profile));

  await logAuditEvent(userId, 'ITEM_ACTIVATED', {
    itemId: target.itemId,
    consumableType: target.type
  });

  return {
    success: true,
    message: {
      en: `Successfully activated ${target.name.en}!`,
      ua: `Успішно активовано: ${target.name.ua}!`
    }
  };
}

/**
 * Audit telemetry and fraud detection logger
 */
export async function logAuditEvent(
  userId: string, 
  eventType: AuditTrackingEvent['eventType'], 
  payload: Record<string, any>,
  seed?: string
) {
  if (!userId) return;

  const eventId = `audit_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;
  const auditEvent: AuditTrackingEvent = {
    id: eventId,
    userId,
    eventType,
    timestamp: Date.now(),
    payload,
    seed,
    clientTelemetry: {
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'Server/Node',
      platform: typeof navigator !== 'undefined' ? navigator.platform : 'Unknown',
      locale: typeof navigator !== 'undefined' ? navigator.language : 'en',
      timestamp: Date.now()
    }
  };

  try {
    const auditRef = doc(db, 'audit_tracking', userId, 'events', eventId);
    await setDocWithQueue(auditRef, auditEvent);
  } catch (e) {
    console.debug('[AuditTelemetry] Log cached locally:', auditEvent);
  }
}
